import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import type { LanguageContext, AppStore } from "./store";
import type { LanguagePack, Route } from "../types";
import { parseRoute } from "../lib/routing.mjs";

export const StoreContext = createContext<AppStore | null>(null);
export const LanguageCtx = createContext<LanguageContext | null>(null);

/** Subscribe to the store; the returned store object is mutable, so read it during render. */
export function useStore(): AppStore {
  const store = useContext(StoreContext);
  if (!store) throw new Error("StoreContext missing");
  useSyncExternalStore(store.subscribe, store.getRevision);
  return store;
}

export function useLanguage(): LanguageContext {
  const ctx = useContext(LanguageCtx);
  if (!ctx) throw new Error("LanguageCtx missing");
  // Pages re-render on store changes through their parent; subscribing here keeps leaf hooks honest.
  useStore();
  return ctx;
}

export function useRoute(store: AppStore): Route {
  const [hash, setHash] = useState(() => location.hash);
  useEffect(() => {
    const onChange = () => setHash(location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return parseRoute(hash === "#main-content" ? "" : hash, store.registry.map((l) => l.id), store.state.selectedLanguageId) as Route;
}

export type PackStatus = { status: "loading" } | { status: "error" } | { status: "ready"; pack: LanguagePack };

export function useLanguagePack(store: AppStore, languageId: string | null, attempt: number): PackStatus {
  const [result, setResult] = useState<{ id: string; attempt: number; value: PackStatus } | null>(null);
  useEffect(() => {
    if (!languageId) return;
    let cancelled = false;
    store.loadPack(languageId).then(
      (pack) => { if (cancelled) return; store.prepareLanguage(pack); setResult({ id: languageId, attempt, value: { status: "ready", pack } }); store.notify(); },
      () => { if (!cancelled) setResult({ id: languageId, attempt, value: { status: "error" } }); },
    );
    return () => { cancelled = true; };
  }, [store, languageId, attempt]);
  if (!languageId || !result || result.id !== languageId || result.attempt !== attempt) return { status: "loading" };
  return result.value;
}
